<template>
    <div class="forum-thread-timeline">
        <div
            ref="stickyCarrier"
            :class="stickyClasses"
            :style="stickyStyle">

            <slot name="topAnchor">
                <button
                    class="top-anchor-btn anchor-btn"
                    :title="startDateStatus"
                    @click="goToFirstPost()">
                    <span v-if="startDate">
                        {{ formatShortDate(startDate) }}
                    </span>
                    <span v-else>
                        Start
                    </span>
                </button>
            </slot>

            <div
                class="slider"
                @mousedown.prevent="onSliderMouseDown">
                <div
                    class="slider-track"
                    ref="sliderTrack">
                </div>
                <div
                    class="slider-thumb"
                    ref="sliderThumb"
                    :style="sliderThumbStyle"
                    @mousedown.prevent="onSliderThumbMouseDown">
                    <div class="slider-thumb-bar"></div>
                    <slot name="sliderThumbLabel">
                        <div class="slider-thumb-label">
                            <div v-if="posts.length > 0">
                                {{ Math.floor(currentIndex) + 1 }}
                                /
                                {{ posts.length }}
                            </div>
                            <div v-else>0 / 0</div>
                            <div
                                :title="formatFullDate(currentDate)"
                                v-if="currentDate">
                                {{ formatShortDate(currentDate) }}
                            </div>
                            <div v-else>
                                <em>No date</em>
                            </div>
                        </div>
                    </slot>
                </div>
            </div>

            <slot name="bottomAnchor">
                <button
                    class="bottom-anchor-btn anchor-btn"
                    :title="lastDateStatus"
                    @click="goToLastPost()">
                    <span v-if="lastDate">
                        {{ formatShortDate(lastDate) }}
                    </span>
                    <span v-else>
                        Last
                    </span>
                </button>
            </slot>

        </div>
    </div>
</template>

<script>
    // ForumThreadTimeline: a navigation element used to slide between posts of
    // a thread. It follows the user as they scroll through the thread,
    // displaying how far into the discussion they are. It also provides
    // controls for quickly moving to particular place in the thread, like the
    // beginning, the end, or some arbitrary position in the middle.

    import ForumUtils from 'src/utils/forum';


    function getScrollPos() {
        return $(window).scrollTop();
    }

    function getTopPos(elem) {
        return $(elem).offset().top;
    }

    function getBottomPos(elem) {
        const $elem = $(elem);
        return $elem.offset().top + $elem.outerHeight();
    }

    function getHeight(elem) {
        return $(elem).outerHeight();
    }

    function throttleToAnimFrame(fn) {
        let sched = false;
        return function() {
            if (!sched) {
                requestAnimationFrame(function() {
                    sched = false;
                    fn();
                });
                sched = true;
            }
        }
    }


    export default {
        props: {
            // The current position within the timeline, usually tied to the
            // scroll position within the document.
            value: {
                // Range: [0, 1]
                type: Number,
                default: 0,
            },
            posts: {
                type: Array,
                required: true,
            },

            // The date of the first post within the thread.
            startDate: {
                type: Date,
                required: false,
            },

            // The date of the last post within the thread.
            lastDate: {
                type: Date,
                required: false,
            },

            // Whether the timeline should stick to the top of the viewport or
            // not.
            //
            // TODO: remove string support. This was added so that the
            // implementor can use v-bind or a plain attribute, but in
            // hindsight, it's unnecessary.
            //
            // TODO: consider whether we can simplify and just remove this
            // option, making it always-on.
            sticky: {
                type: [Boolean, String],
                default: false,
                validator(value) {
                    // If string, must be true or false
                    if (typeof value === 'string') {
                        return /^true|false$/.test(value);
                    } else {
                        return true;
                    }
                },
            },

            // If sticky is set, this configures how much padding to maintain
            // between this component and the top of the viewport.
            //
            // TODO: consider whether we can simplify and just remove this
            // option, using the default value.
            stickyPadding: {
                type: [Number, String],
                default: 40,
                validator(value) {
                    // If string, must be in form "40" or "40px"; no other units
                    // are allowed, as they would muck up the sticky
                    // calculations.
                    if (typeof value === 'string') {
                        return /^-?[0-9]+(?:\.?[0-9]+)?(?:px)?$/.test(value);
                    } else {
                        return true;
                    }
                },
            },

            // If sticky is set, this flag will allow the timeline to stick to
            // the top of the viewport only until it reaches the bottom of the
            // parent element.
            //
            // TODO: remove string support. This was added so that the
            // implementor can use v-bind or a plain attribute, but in
            // hindsight, it's unnecessary.
            //
            // TODO: consider whether we can simplify and just remove this
            // option, making it always-on.
            stayInParent: {
                // Parent must have relative or absolute positioning
                type: [Boolean, String],
                default: false,
                validator(value) {
                    // If string, must be true or false
                    if (typeof value === 'string') {
                        return /^true|false$/.test(value);
                    } else {
                        return true;
                    }
                },
            },
        },

        data() {
            return {
                // When `sticky` is enabled, the timeline will stick to the top
                // of the screen when scrolled. When `stayInParent` is enabled,
                // the timeline will stop at the bottom of the parent element
                // when scrolled.
                // Possible values: null, 'screen', 'parent'
                stuckTo: null,

                // The timeline has the ability to scroll the page (although
                // that isn't implemented by this component directly), which
                // can potentially move the timeline elements while the user is
                // trying to use the slider. This makes the UI kind of awkward.
                // To avoid this, we can stick the timeline to the screen while
                // the user is holding the slider thumb, and then reposition
                // elements after the user releases it. This requires `sticky`
                // to be enabled.
                stickyOffset: null,

                // When `stuckTo` is set to 'screen', the position of the
                // carrier is set to fixed. As long as we don't explicitly set
                // the horizontal position, it will match the root element,
                // which is what we're after. However, this only holds true
                // UNTIL there is a horizontal scrollbar somewhere, like on
                // #app; at that point, the horizontal position differs from
                // root. To account for that case, we track the position of
                // the root element relative to the right side of the viewport.
                rootRightPosRelScreen: 0,

                // In order to adjust the slider thumb with the mouse, we need
                // to detect when the slider or slider thumb has been been
                // clicked (since the mouse isn't guaranteed to remain over the
                // elements while sliding).
                sliderThumbHeld: false,

                // While the slider is held, an internal value is used instead
                // of the prop value. While the prop value is always restricted
                // to the range [0, 1], the internal value is unclamped. This
                // allows us to track the value of the slider even when the user
                // drags their mouse past the beginning or end of the
                // discussion, and if/when they drag it back within range the
                // slider will seamlessly continue updating.
                internalValue: this.clampedValue,

                // The sliderThumbStyle computed property depends on $refs, and
                // since $refs aren't reactive, we use this hack to recalculate
                // the style after the refs are set.
                isMounted: false,

                // We throttle certain callbacks to match animation frames for
                // performance reasons.
                throttledOnScroll: null,
                throttledOnResize: null,

            };
        },

        mounted() {
            this.throttledOnScroll = throttleToAnimFrame(this.onScroll);
            this.throttledOnResize = throttleToAnimFrame(this.onResize);
            window.addEventListener('scroll', this.throttledOnScroll);
            window.addEventListener('resize', this.throttledOnResize);
            window.addEventListener('mouseup', this.onMouseUp);
            window.addEventListener('mousemove', this.onMouseMove);
            this.updateRootRightPosition();
            this.updateSyncedValue();
            this.updateStickyMode();
            this.isMounted = true;
        },

        beforeDestroy() {
            window.removeEventListener('scroll', this.throttledOnScroll);
            window.removeEventListener('resize', this.throttledOnResize);
            window.removeEventListener('mouseup', this.onMouseUp);
            window.removeEventListener('mousemove', this.onMouseMove);
        },

        methods: {
            formatFullDate(date) {
                return ForumUtils.formatFullDate(date);
            },

            formatShortDate(date) {
                return ForumUtils.formatRecentDate(date);
            },

            onScroll() {
                this.updateRootRightPosition();
                this.updateSyncedValue();
                this.updateStickyMode();
            },

            onResize() {
                this.updateRootRightPosition();
                this.updateSyncedValue();
                this.updateStickyMode();
            },

            onMouseUp() {
                this.releaseSliderThumb();
            },

            onMouseMove(ev) {
                if (this.sliderThumbHeld) {
                    // We update the value prop using delta mouse coords. This
                    // was originally done using absolute mouse coords, and
                    // transforming those to coords relative to the slider, but
                    // that approach caused all sorts of bugs whenever the
                    // position of the slider wasn't fixed.
                    this.moveThumbByDeltaY(ev.movementY);
                }
            },

            onSliderMouseDown(ev) {
                if (!this.sliderThumbHeld) {

                    // Grab the thumb
                    this.grabSliderThumb();

                    // Center the thumb vertically on the mouse cursor
                    this.centerThumbOnY(ev.pageY);
                }
            },

            onSliderThumbMouseDown(ev) {
                if (!this.sliderThumbHeld) {

                    // Grab the thumb where it is, but don't move it yet
                    this.grabSliderThumb();
                }
            },

            grabSliderThumb() {
                if (!this.sliderThumbHeld) {
                    this.sliderThumbHeld = true;
                    this.internalValue = this.clampedValue;

                    // Stick slider to screen, wherever it is right now. We
                    // don't want the slider moving around while the user is
                    // trying to use it.
                    if (this.sticky) {
                        const elem = this.$refs.stickyCarrier;
                        const bounds = elem.getBoundingClientRect();
                        this.stickyOffset = bounds.top;
                        this.stuckTo = 'screen';
                    }

                    this.$emit('thumbgrab');
                }
            },

            releaseSliderThumb() {
                if (this.sliderThumbHeld) {
                    this.sliderThumbHeld = false;
                    this.stickyOffset = null;
                    this.$emit('thumbrelease');
                    this.updateStickyMode();
                }
            },

            centerThumbOnY(y) {
                const sliderTrackTop = getTopPos(this.$refs.sliderTrack);
                const sliderTrackHeight = getHeight(this.$refs.sliderTrack);
                const sliderThumbHeight = getHeight(this.$refs.sliderThumb);
                const activeHeight = sliderTrackHeight - sliderThumbHeight;

                const yRelTrack = y - sliderTrackTop;
                const adjYRelTrack = (
                    yRelTrack - sliderThumbHeight / 2
                );
                const rawValue = adjYRelTrack / activeHeight;
                this.internalValue = rawValue;

                const newValue = Math.max(0, Math.min(1, this.internalValue));
                if (newValue !== this.value) {
                    this.$emit('input', newValue);
                }
            },

           moveThumbByDeltaY(deltaY) {
                const sliderTrackHeight = getHeight(this.$refs.sliderTrack);
                const sliderThumbHeight = getHeight(this.$refs.sliderThumb);
                const activeHeight = sliderTrackHeight - sliderThumbHeight;

                const rawValueDiff = deltaY / activeHeight;
                this.internalValue = this.internalValue + rawValueDiff;

                const newValue = Math.max(0, Math.min(1, this.internalValue));
                if (newValue !== this.value) {
                    this.$emit('input', newValue);
                }
            },

            updateRootRightPosition() {
                const bounds = this.$el.getBoundingClientRect();
                const viewWidth = document.documentElement.clientWidth;
                this.rootRightPosRelScreen = viewWidth - bounds.right;
            },

            updateSyncedValue() {
                if (this.syncValueWithScroll === true ||
                    this.syncValueWithScroll === 'true') {
                    const parentElem = this.$el.parentElement;
                    const scrollPos = getScrollPos();
                    const topPos = getTopPos(parentElem);
                    const bottomPos = getBottomPos(parentElem);
                    if (scrollPos < topPos) {
                        this.$emit('input', 0);
                    } else if (scrollPos > bottomPos) {
                        this.$emit('input', 1);
                    } else {
                        const p = scrollPos - topPos;
                        const d = bottomPos - topPos;
                        this.$emit('input', p / d);
                    }
                }
            },

            updateStickyMode() {
                if (this.sticky === true || this.sticky === 'true') {
                    if (this.stickyOffset !== null) {
                        // Sticky offset will handle the mode itself, and it
                        // will call this method again when it's been turned
                        // off.
                        return;
                    }

                    const padding = this.stickyPaddingNum;
                    const scrollPos = getScrollPos();
                    const minTopPos = getTopPos(this.$el) - padding;

                    if (this.stayInParent) {
                        const height = getHeight(this.$refs.stickyCarrier);
                        const bottom = getBottomPos(this.$el.parentElement);
                        const maxTopPos = bottom - height - padding;

                        if (scrollPos <= minTopPos) {
                            this.stuckTo = null;
                        } else if (scrollPos > minTopPos &&
                                   scrollPos < maxTopPos) {
                            this.stuckTo = 'screen';
                        } else {
                            this.stuckTo = 'parent';
                        }

                    } else /* need not stay in parent */ {
                        if (scrollPos <= minTopPos) {
                            this.stuckTo = null;
                        } else {
                            this.stuckTo = 'screen';
                        }
                    }

                } else /* not sticky */ {
                    this.stuckTo = null;
                }
            },

            async goToFirstPost() {
                // We kind of shoehorn this "go-to-first-post" functionality in
                // using out knowledge that 0 will always represent the
                // beginning of the first post.
                await this.goToValue(0);
            },

            async goToLastPost() {
                // Similar to `goToFirstPost()`, we're shoehorning in the
                // "go-to-last-post" by abusing the `goToValue()` functionality,
                // assuming that 1 will always represent the end of the thread.
                // However, in this case we want to go to the beginning of the
                // last post, rather than the end of the thread.
                let newValue = 1;
                if (this.posts.length > 0) {
                    const postCount = this.posts.length;
                    newValue = (postCount - 1) / postCount;
                }
                await this.goToValue(newValue);
            },

            async goToValue(value) {
                // This simulates the slider actions so that the parent
                // components don't have to implement custom logic to handle
                // timeline buttons.
                this.$emit('thumbgrab');
                await this.$nextTick();
                this.$emit('input', value);
                await this.$nextTick();
                this.$emit('thumbrelease');
            },
        },

        computed: {
            startDateStatus() {
                if (this.startDate) {
                    const fd = this.formatFullDate(this.startDate);
                    return `Thread created on ${fd}`;
                } else {
                    return null;
                }
            },

            lastDateStatus() {
                if (this.lastDate) {
                    const fd = this.formatFullDate(this.lastDate);
                    return `Last post created on ${fd}`;
                } else {
                    return null;
                }
            },

            clampedValue() {
                return Math.max(0, Math.min(1, this.value));
            },

            currentIndex() {
                if (this.posts.length > 0) {
                    return Math.min(
                        Math.floor(this.value * this.posts.length),
                        this.posts.length - 1,
                    );
                } else {
                    return null;
                }
            },

            currentDate() {
                if (this.currentIndex !== null) {
                    return this.posts[this.currentIndex].createdAt;
                } else {
                    return null;
                }
            },

            stickyPaddingNum() {
                if (typeof this.stickyPadding === 'number') {
                    return this.stickyPadding;
                } else {
                    return parseFloat(this.stickyPadding);
                }
            },

            stickyClasses() {
                return {
                    'sticky-carrier': true,
                    'stuck-to-top': this.stuckTo === 'screen',
                    'stuck-to-bottom': this.stuckTo === 'parent',
                };
            },

            stickyStyle() {
                if (this.stickyOffset !== null) {
                    return {
                        'top': `${this.stickyOffset}px`,
                        'right': `${this.rootRightPosRelScreen}px`,
                    };
                } else if (this.stuckTo === 'screen') {
                    return {
                        'top': `${this.stickyPaddingNum}px`,
                        'right': `${this.rootRightPosRelScreen}px`,
                    };
                } else if (this.stuckTo === 'parent') {
                    return {};
                } else {
                    return {};
                }
            },

            sliderThumbStyle() {
                if (this.isMounted) {
                    const value = this.clampedValue;
                    const axisHeight = getHeight(this.$refs.sliderTrack);
                    const handleHeight = getHeight(this.$refs.sliderThumb);
                    const offsetPx = value * (axisHeight - handleHeight);
                    return {
                        transform: `translateY(${offsetPx}px)`,
                    };
                } else {
                    return {};
                }
            },
        },
    }
</script>

<style lang="scss" scoped>
    $accent: #33ccff;
    $dim-accent: #2b9ec7; // Darker #33ccff

    .forum-thread-timeline {
        height: 360px;
        width: 80px;
        min-width: 80px;
    }

    .sticky-carrier {
        width: inherit; // Want to keep dimensions set on root elem
        height: inherit;
        display: flex;
        flex-direction: column;
        white-space: nowrap;

        &.stuck-to-top {
            position: fixed;
            top: 0;
        }

        &.stuck-to-bottom {
            position: absolute;
            bottom: 0;
        }
    }

    .anchor-btn {
        background: none;
        border: none;
        padding: 0;
        text-align: left;
    }

    .top-anchor-btn {
        margin-bottom: 10px;
    }

    .bottom-anchor-btn {
        margin-top: 10px;
    }

    .slider {
        flex: 1;
        cursor: ns-resize;
        position: relative;

        &:hover {
            .slider-track {
                background-color: $accent;
            }
            .slider-thumb-bar {
                border-color: $accent;
            }
        }
    }

    .slider-track {
        position: absolute;
        top: 0;
        left: 5px;
        bottom: 0;
        width: 1px;
        background-color: $dim-accent;
    }

    .slider-thumb {
        display: flex;

        .slider-thumb-bar {
            padding-right: 3px;
            border-right: 5px solid $dim-accent;
        }

        .slider-thumb-label {
            margin-left: 15px;
            padding: 5px 0;
        }
    }
</style>



// WEBPACK FOOTER //
// thread-timeline.vue?f24c8054